import { GoogleOneTapIntermediate } from "@circle-react/components/OnboardingComponents/GoogleOneTapIntermediate";
import { ConfirmLastLoginMethod } from "@circle-react/components/OnboardingComponents/LoginOptions/ConfirmLastLoginMethod";
import { setDateFnsOptions } from "@circle-react/config/Locales/DateFns";
import { setFlatpickrLocale } from "@circle-react/config/Locales/Flatpickr";
import { initializeBugsnag } from "@circle-react/helpers/bugsnagHelpers";
import { initializeHyperDX } from "@circle-react/helpers/hyperdxHelpers";
import { MainAppProviders } from "@circle-react/providers/MainApp";
import { applyV2Colors } from "@circle-react/providers/Theme";
import { useEffect } from "react";

import { SomethingWentWrong } from "@/react/components/shared/ErrorPages/SomethingWentWrong";
import { withErrorBoundary } from "@/react/components/shared/ErrorPages/WithErrorBoundary";
import type { BackendProps } from "@/react/providers/AppContext";
import { ContentPropsProvider } from "@/react/providers/MainApp/ContentPropsProvider";

import { MainAppRouter } from "./MainAppRouter";

initializeBugsnag();
initializeHyperDX();

export type MainAppProps = BackendProps;

// This component is mapped at app/javascript/controllers/react_mount_controller.js
// And rendered at app/views/shared/_react_app.html.erb
const MainApp = ({
  notice,
  alert,
  display_community_switcher = false,
  display_header = true,
  minimal_layout = false,
  ...rest
}: MainAppProps) => {
  useEffect(() => {
    applyV2Colors();
    void setDateFnsOptions();
    void setFlatpickrLocale();
  }, []);

  const contentProps = {
    notice,
    alert,
    shouldDisplayHeader: display_header,
    shouldDisplayCommunitySwitcher: display_community_switcher,
    hasMinimalLayout: minimal_layout,
  };

  const backendProps: BackendProps = {
    notice,
    alert,
    display_community_switcher,
    display_header,
    minimal_layout,
    ...rest,
  };

  return (
    <MainAppProviders backendProps={backendProps}>
      <ContentPropsProvider contentProps={contentProps}>
        <ConfirmLastLoginMethod />
        <MainAppRouter />
        {rest.google_one_tap_iframe_src && (
          <GoogleOneTapIntermediate
            iframeSrc={rest.google_one_tap_iframe_src}
          />
        )}
      </ContentPropsProvider>
    </MainAppProviders>
  );
};

export const App = withErrorBoundary(MainApp, props => (
  <SomethingWentWrong {...props} fullHeight />
));
